import { createSignal, type JSX, onMount, Show } from 'solid-js';
import { t } from '../i18n';
import { Field } from './Field';
import { Button, buttonClass } from './ui/Button';

// A machine's rename form (#6566, prototype `panel()` `form.rename`): opened by the pencil
// beside the details panel's heading, or by the card menu's "Rename" (the panel in rename
// mode). Presenter: `createMachineRenameWrite` (`lib/name-write.ts`) owns the write, trims
// what Save hands it, and sends `null` for an empty field.
//
// THE HINT NAMES WHERE EMPTY GOES BACK TO: "Leave it empty to go back to Swift Nova.", the
// generated name the server fills for an unnamed machine (`generatedName`, `lib/fleet-rename.ts`).
// The field opens focused with its text selected, so typing replaces the name.

export const MACHINE_RENAME_INPUT = 'machine-rename';

export function MachineRename(props: {
  /** The name the machine shows today. */
  readonly currentName: string;
  /** The generated name an empty field goes back to. */
  readonly generatedName: string;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onSave: (text: string) => void;
  readonly onCancel: () => void;
}): JSX.Element {
  const [text, setText] = createSignal(props.currentName);
  let input: HTMLInputElement | undefined;
  onMount(() => {
    input?.focus();
    input?.select();
  });

  const submit = (e: Event): void => {
    e.preventDefault();
    if (props.pending) return;
    props.onSave(text());
  };

  return (
    <form class="field-group box-manage__form" onSubmit={submit}>
      <Field
        label={t('fleet.panel.rename_label')}
        id={MACHINE_RENAME_INPUT}
        desc={t('fleet.panel.rename_desc', { name: props.generatedName })}
      >
        {(a11y) => (
          <input
            {...a11y}
            ref={input}
            class="input"
            type="text"
            value={text()}
            onInput={(e) => setText(e.currentTarget.value)}
          />
        )}
      </Field>

      <Show when={props.error}>
        {(msg) => (
          <p class="field__error" role="alert">
            {msg()}
          </p>
        )}
      </Show>

      <div class="box-manage__actions">
        <button type="submit" class={buttonClass({ size: 'sm' })} disabled={props.pending}>
          {t(props.pending ? 'fleet.panel.rename_saving' : 'fleet.panel.rename_save')}
        </button>
        <Button variant="ghost" size="sm" onClick={props.onCancel}>
          {t('fleet.panel.rename_cancel')}
        </Button>
      </div>
    </form>
  );
}
